/** Shared, pure quote protection. Rates are editable assumptions, not market benchmarks. */
export const OPERATING_SERVICES = {
  welding: 'Welding', semis: 'Semi hauling', hotshots: 'Hotshots', grocery: 'Grocery delivery',
  tacos: 'Food delivery', water: 'Water delivery', waste_management: 'Waste management',
  porta_potty: 'Portable toilets', other: 'Other field service',
} as const;
export type OperatingService = keyof typeof OPERATING_SERVICES;
export type VehicleRates = {
  fuelType: 'diesel' | 'gasoline'; mpg: number;
  vehicleBasis: 'hour' | 'mile'; vehicleHourlyUsd: number; wearPerMileUsd: number;
  laborHourlyUsd: number; servicePerMileUsd: number; minimumUsd: number;
};
export type ServiceRateProfiles = Partial<Record<OperatingService, VehicleRates>>;
export type FuelSnapshot = { asOf: string; fetchedAt: string; gasoline: number; diesel: number; source: string; region: string };
export const FUEL_SOURCE = 'https://www.eia.gov/petroleum/gasdiesel/';
export function operatingService(raw: unknown): OperatingService {
  const s = String(raw || '').toLowerCase();
  if (s in OPERATING_SERVICES) return s as OperatingService;
  if (/weld/.test(s)) return 'welding';
  if (/semi|heavy_haul/.test(s)) return 'semis';
  if (/hotshot/.test(s)) return 'hotshots';
  if (/grocery/.test(s)) return 'grocery';
  if (/food|taco/.test(s)) return 'tacos';
  if (/water/.test(s)) return 'water';
  if (/dumpster|waste/.test(s)) return 'waste_management';
  if (/porta/.test(s)) return 'porta_potty';
  return 'other';
}
export function defaultVehicleRates(service: OperatingService): VehicleRates {
  const car = service === 'grocery' || service === 'tacos';
  const heavy = service === 'semis' || service === 'waste_management';
  return { fuelType: car ? 'gasoline' : 'diesel', mpg: car ? 22 : heavy ? 7 : 10,
    vehicleBasis: car ? 'mile' : 'hour', vehicleHourlyUsd: car ? 0 : 40,
    wearPerMileUsd: car ? 0.25 : heavy ? 0.65 : 0.4,
    laborHourlyUsd: service === 'welding' ? 100 : car ? 20 : 35,
    servicePerMileUsd: 0, minimumUsd: 0 };
}
const finite = (v: unknown, lo: number, hi: number, fallback: number) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? Math.round(v * 100) / 100 : fallback;
export function normalizeVehicleRates(raw: unknown, service: OperatingService): VehicleRates {
  const d = defaultVehicleRates(service);
  const v = raw && typeof raw === 'object' ? raw as Partial<VehicleRates> : {};
  return { fuelType: v.fuelType === 'gasoline' || v.fuelType === 'diesel' ? v.fuelType : d.fuelType,
    mpg: finite(v.mpg, 1, 100, d.mpg), vehicleBasis: v.vehicleBasis === 'mile' || v.vehicleBasis === 'hour' ? v.vehicleBasis : d.vehicleBasis,
    vehicleHourlyUsd: finite(v.vehicleHourlyUsd, 0, 2000, d.vehicleHourlyUsd), wearPerMileUsd: finite(v.wearPerMileUsd, 0, 50, d.wearPerMileUsd),
    laborHourlyUsd: finite(v.laborHourlyUsd, 0, 2000, d.laborHourlyUsd), servicePerMileUsd: finite(v.servicePerMileUsd, 0, 100, 0), minimumUsd: finite(v.minimumUsd, 0, 50000, 0) };
}
export function normalizeServiceProfiles(raw: unknown): ServiceRateProfiles {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).filter(([k]) => k in OPERATING_SERVICES).map(([k, v]) => [k, normalizeVehicleRates(v, k as OperatingService)]));
}
export function fuelIsFresh(fuel: FuelSnapshot, now = Date.now()): boolean {
  const age = now - Date.parse(fuel.asOf + 'T00:00:00Z');
  return Number.isFinite(age) && age >= -86400000 && age <= 14 * 86400000 && [fuel.gasoline, fuel.diesel].every(v => Number.isFinite(v) && v >= 0.5 && v <= 30);
}
/** EIA's public RSS carries weekly national regular gasoline and on-highway diesel averages. */
export function parseEiaFuelRss(xml: string, now = new Date()): FuelSnapshot {
  const date = xml.match(/Data For (\d{2})\/(\d{2})\/(\d{2,4})/i);
  const gasoline = xml.match(/Regular Gasoline Retail Price[\s\S]*?(\d+\.\d+)\s*\.+\s*U\.S\./i);
  const diesel = xml.match(/On-Highway Diesel Fuel Retail Price[\s\S]*?(\d+\.\d+)\s*\.+\s*U\.S\./i);
  if (!date || !gasoline || !diesel) throw new Error('Fuel publication could not be read.');
  const year = date[3].length === 2 ? '20' + date[3] : date[3];
  const fuel: FuelSnapshot = { asOf: `${year}-${date[1]}-${date[2]}`, fetchedAt: now.toISOString(), gasoline: Number(gasoline[1]), diesel: Number(diesel[1]), source: FUEL_SOURCE, region: 'U.S. weekly average' };
  if (!fuelIsFresh(fuel, now.getTime())) throw new Error('Fuel publication is out of date. Try again later.');
  return fuel;
}
/** Official EIA API counterpart to the RSS feed; require matching national retail series/dates. */
export function parseEiaFuelApi(raw: unknown, now = new Date()): FuelSnapshot {
  const data = (raw as { response?: { data?: unknown[] } })?.response?.data;
  if (!Array.isArray(data)) throw new Error('Fuel API publication could not be read.');
  const rows = data.filter((r): r is Record<string, unknown> => Boolean(r && typeof r === 'object'));
  const gas = rows.find(r => r.series === 'EMM_EPMR_PTE_NUS_DPG' && r.duoarea === 'NUS' && r.units === '$/GAL');
  const diesel = rows.find(r => r.series === 'EMD_EPD2D_PTE_NUS_DPG' && r.duoarea === 'NUS' && r.units === '$/GAL');
  if (!gas || !diesel || gas.period !== diesel.period || !/^\d{4}-\d{2}-\d{2}$/.test(String(gas.period))) throw new Error('Fuel API requires matching weekly retail prices.');
  const fuel: FuelSnapshot = { asOf: String(gas.period), fetchedAt: now.toISOString(), gasoline: Number(gas.value), diesel: Number(diesel.value), source: FUEL_SOURCE, region: 'U.S. weekly average' };
  if (!fuelIsFresh(fuel, now.getTime())) throw new Error('Fuel API publication is out of date.');
  return fuel;
}
export type OperatingScope = { miles: number; hours: number; description: string };
export function calculateOperatingProtection(input: { baseCents: number; excludedCents?: number; scope: OperatingScope; rates: VehicleRates; fuel: FuelSnapshot; feeBps?: number }) {
  const { scope, rates, fuel } = input;
  if (![scope.miles, scope.hours, input.baseCents, input.excludedCents ?? 0].every(v => Number.isFinite(v) && v >= 0) || scope.miles > 20000 || scope.hours > 10000) throw new Error('Valid travel miles and work hours are required.');
  if (rates.mpg <= 0) throw new Error('Valid MPG required.');
  const cents = (n: number) => Math.ceil(n * 100 - 1e-8);
  const fuelCents = cents(scope.miles / rates.mpg * fuel[rates.fuelType]);
  // The hourly vehicle charge covers wear, tires, repairs, insurance and equipment.
  // A per-mile reserve is an alternative, never added on top of that hourly charge.
  const vehicleCents = cents(rates.vehicleBasis === 'hour' ? scope.hours * rates.vehicleHourlyUsd : scope.miles * rates.wearPerMileUsd);
  const laborCents = cents(scope.hours * rates.laborHourlyUsd);
  const operatingCents = fuelCents + vehicleCents + laborCents;
  const contractorMinimumCents = Math.max(operatingCents, cents(scope.miles * rates.servicePerMileUsd), cents(rates.minimumUsd));
  const feeBps = input.feeBps ?? 1000;
  if (!Number.isFinite(feeBps) || feeBps < 0 || feeBps >= 10000) throw new Error('Invalid platform fee');
  const minimumServiceCents = Math.ceil(contractorMinimumCents / (1 - feeBps / 10000));
  const excludedCents = Math.round(input.excludedCents ?? 0);
  const baseCents = Math.round(input.baseCents);
  const totalCents = Math.max(baseCents, excludedCents + minimumServiceCents);
  return { baseCents, totalCents, upliftCents: totalCents - baseCents, excludedCents, fuelCents, vehicleCents, laborCents, operatingCents, contractorMinimumCents, minimumServiceCents, feeBps, scope, rates, fuel };
}
export type ProtectedCost = ReturnType<typeof calculateOperatingProtection>;
